import { useState, useEffect, useRef } from "react";
import { X, MessageCircle, Copy, Check } from "lucide-react";
import { useTranslation } from "react-i18next";

const WA_LINK = "https://wa.me/33622862400";
const WA_PHONE = "+33 6 22 86 24 00";

// QR Code via API
const QR_CODE_URL = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(WA_LINK)}&format=svg`;

// Detect if mobile device
const isMobile = () => {
  if (typeof window === "undefined") return false;
  return window.innerWidth < 768 || /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
};

const WhatsAppButton = () => {
  const { t } = useTranslation();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const popupRef = useRef<HTMLDivElement>(null);

  const handleClick = () => {
    if (isMobile()) {
      // On mobile, open WhatsApp directly
      window.open(WA_LINK, "_blank", "noopener,noreferrer");
    } else {
      // On desktop, show popup
      setIsPopupOpen(true);
    }
  };

  const closePopup = () => setIsPopupOpen(false);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(WA_LINK);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = WA_LINK;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isPopupOpen) {
        closePopup();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isPopupOpen]);

  return (
    <>
      {/* WhatsApp Button */}
      <div className="fixed bottom-5 right-5 z-[9999]">
        <div className="relative group">
          {/* Tooltip - Desktop (left side) */}
          <span className="absolute right-[75px] top-1/2 -translate-y-1/2 bg-[#1a2744] text-white px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 pointer-events-none hidden md:block">
            {t("whatsapp.tooltip", "Discutez avec David, notre dirigeant")}
            {/* Arrow pointing right */}
            <span className="absolute right-[-6px] top-1/2 -translate-y-1/2 border-[6px] border-solid border-transparent border-l-[#1a2744]" />
          </span>
          
          {/* Tooltip - Mobile (left side) */}
          <span className="absolute right-[75px] top-1/2 -translate-y-1/2 bg-[#1a2744] text-white px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible group-active:opacity-100 group-active:visible transition-all duration-200 pointer-events-none md:hidden">
            {t("whatsapp.tooltip", "Discutez avec David, notre dirigeant")}
            {/* Arrow pointing right */}
            <span className="absolute right-[-6px] top-1/2 -translate-y-1/2 border-[6px] border-solid border-transparent border-l-[#1a2744]" />
          </span>
          
          {/* Button */}
          <button
            onClick={handleClick}
            className="w-[60px] h-[60px] bg-gradient-to-br from-[#25D366] to-[#128C7E] rounded-full flex items-center justify-center shadow-[0_4px_15px_rgba(37,211,102,0.5)] hover:shadow-[0_8px_25px_rgba(37,211,102,0.7)] hover:scale-110 transition-all duration-300 animate-wa-pulse"
            aria-label="Contact WhatsApp"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="35" height="35" viewBox="0 0 24 24" fill="white">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Desktop Popup Modal */}
      {isPopupOpen && (
        <>
          {/* Overlay */}
          <div
            className="fixed inset-0 bg-black/50 z-[10000] animate-in fade-in duration-200"
            onClick={closePopup}
            aria-hidden="true"
          />

          {/* Popup */}
          <div
            ref={popupRef}
            className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[10001] bg-white rounded-2xl shadow-[0_10px_40px_rgba(0,0,0,0.3)] p-6 w-[90vw] max-w-[400px] animate-in fade-in zoom-in-95 duration-200"
          >
            {/* Close button */}
            <button
              onClick={closePopup}
              className="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
              aria-label="Fermer"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>

            {/* Header */}
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-[#25D366] to-[#128C7E] rounded-full flex items-center justify-center mx-auto mb-4">
                <MessageCircle className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-1">
                {t("whatsapp.popupTitle", "Contactez David sur WhatsApp")}
              </h3>
              <p className="text-sm text-gray-500">
                {t("whatsapp.popupSubtitle", "Choisissez votre méthode préférée :")}
              </p>
            </div>

            {/* Copy Link Button */}
            <button
              onClick={copyLink}
              className="w-full flex items-center justify-center gap-3 py-3.5 px-4 bg-gradient-to-r from-[#25D366] to-[#128C7E] text-white font-semibold rounded-xl hover:opacity-90 transition-opacity mb-4"
            >
              {copied ? (
                <Check className="w-6 h-6" />
              ) : (
                <Copy className="w-6 h-6" />
              )}
              {copied 
                ? t("whatsapp.linkCopied", "Lien copié !") 
                : t("whatsapp.copyLink", "Copier le lien")}
            </button>

            {/* Divider */}
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-1 h-px bg-gray-200" />
              <span className="text-xs text-gray-400 uppercase">ou</span>
              <div className="flex-1 h-px bg-gray-200" />
            </div>

            {/* QR Code */}
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-3">
                {t("whatsapp.scanQrText", "Ou scannez ce QR code avec votre téléphone")}
              </p>
              <div className="bg-gray-50 rounded-xl p-4 inline-block">
                <img
                  src={QR_CODE_URL}
                  alt="QR Code WhatsApp"
                  className="w-40 h-40 mx-auto"
                  loading="lazy"
                />
              </div>
              <p className="text-base font-semibold text-gray-800 mt-4">
                {WA_PHONE}
              </p>
            </div>
          </div>
        </>
      )}

      {/* Pulse animation style */}
      <style>{`
        @keyframes wa-pulse {
          0%, 100% { 
            transform: scale(1); 
            box-shadow: 0 4px 10px rgba(37, 211, 102, 0.4);
          }
          50% { 
            transform: scale(1.08); 
            box-shadow: 0 6px 20px rgba(37, 211, 102, 0.6);
          }
        }
        .animate-wa-pulse {
          animation: wa-pulse 2s ease-in-out infinite;
        }
        .animate-wa-pulse:hover {
          animation: none;
        }
      `}</style>
    </>
  );
};

export default WhatsAppButton;
